import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  Injector,
  Type
} from '@angular/core';
import { MP_SNACKBAR_DATA, MP_SNACKBAR_REF } from './mp-snackbar.tokens';
import { MpSnackbarOptions, MpSnackbarTypes } from './mp-snackbar.model';
import { MpSnackbarComponent } from './mp-snackbar.component';
import { MpSnackbarRef } from './mp-snackbar-ref.model';
import { ComponentType } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class MpSnackbarService {

  private componentRef: ComponentRef<unknown> = undefined;
  private componentRefVisibilityTimeout: ReturnType<typeof setTimeout> = undefined;
  private environmentInjector = inject(EnvironmentInjector);
  private document = inject(DOCUMENT);
  private applicationRef = inject(ApplicationRef);

  /** Snackbar simple avec message texte */
  public openSnackbar<TData = unknown>(
      message: string,
      type: MpSnackbarTypes = MpSnackbarTypes.SUCCESS,
      options?: MpSnackbarOptions<TData>
  ): void {
    const ref = new MpSnackbarRef<MpSnackbarComponent>(() => this.destroy());
    this.createSnackbar(MpSnackbarComponent, ref, {
      data: options?.data,
      panelClass: options?.panelClass,
      message,
      type,
      duration: options?.duration
    });
  }

  /** Snackbar avec composant custom */
  public openSnackbarFromComponent<TComponent, TData = unknown>(
      component: ComponentType<TComponent>,
      options?: MpSnackbarOptions<TData>
  ): void {
    const ref = new MpSnackbarRef<TComponent>(() => this.destroy());
    this.createSnackbar(MpSnackbarComponent, ref, {
      data: options?.data,
      panelClass: options?.panelClass,
      innerComponent: component,
      duration: options?.duration
    });
  }

  /** Méthode générique pour créer un snackbar et l’attacher au DOM */
  private createSnackbar<TComponent>(
      wrapperComponent: Type<TComponent>,
      snackbarRef: MpSnackbarRef<TComponent>,
      config: {
        message?: string,
        type?: MpSnackbarTypes,
        panelClass?: string | string[],
        innerComponent?: Type<any>,
        data?: unknown,
        duration?: number
      }
  ): void {
    this.destroy();

    const injector = Injector.create({
      parent: this.environmentInjector,
      providers: [
        { provide: MP_SNACKBAR_DATA, useValue: config.data },
        { provide: MP_SNACKBAR_REF, useValue: snackbarRef }
      ]
    });

    this.componentRef = createComponent(wrapperComponent, {
      environmentInjector: this.environmentInjector,
      elementInjector: injector
    });

    // Set inputs
    if (config.message) {
      this.componentRef.setInput('message', config.message);
    }
    if (config.type) {
      this.componentRef.setInput('type', config.type);
    }
    if (config.panelClass) {
      this.componentRef.setInput('panelClass', config.panelClass);
    }
    if (config.innerComponent) {
      this.componentRef.setInput('innerComponent', config.innerComponent);
    }

    this.applicationRef.attachView(this.componentRef.hostView);

    const visibilityDuration = config.duration ?? 3000;
    this.componentRefVisibilityTimeout = setTimeout(() => this.destroy(), visibilityDuration);

    this.document.body.appendChild(this.componentRef.location.nativeElement);
  }

  /** Détruit le snackbar en cours */
  private destroy(): void {
    if (this.componentRef) {
      this.applicationRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = undefined;
    }
    if (this.componentRefVisibilityTimeout) {
      clearTimeout(this.componentRefVisibilityTimeout);
      this.componentRefVisibilityTimeout = undefined;
    }
  }
}
